import { createContext, useContext, useMemo, type ReactNode } from 'react';

/**
 * The single source of truth for "which org am I currently working in".
 *
 * `SignedInShell` owns this value as per-tab React state — it is set on the
 * initial org pick, on every org switch (`beginSwitch`), and from the /invite
 * one-shot hint. Historically every org-scoped surface (Documents, Tasks, the
 * Settings area, Members, Account, …) ignored it and instead re-derived its
 * own "active org" as the *oldest* membership (`memberships` ordered by
 * `created_at` ascending, take `[0]`). That meant switching orgs left those
 * surfaces pinned to the home org — you'd switch and still see the other org's
 * documents, and Settings would mutate the wrong org.
 *
 * The fix: the shell publishes the active org here once, and every surface
 * reads it via {@link useActiveOrg}. One owner, many readers — no divergence.
 */
export interface ActiveOrg {
  /** The org the viewer is currently working in. `null` while the session is
   *  still resolving or before an initial org has been picked. */
  activeOrgId: string | null;
  /** Display name of the active org — same source as the header chrome. */
  activeOrgName: string | null;
}

const ActiveOrgContext = createContext<ActiveOrg | null>(null);

/**
 * Publishes the shell-owned active org to every signed-in surface. Mounted by
 * `SignedInShell` around its whole subtree, so any page (and the view-model
 * hooks it calls) can read the current org without prop-drilling.
 */
export function ActiveOrgProvider({
  activeOrgId,
  activeOrgName,
  children
}: ActiveOrg & { children: ReactNode }) {
  // Memoize on the primitive fields so consumers re-render only when the org
  // actually changes, not on every unrelated shell re-render.
  const value = useMemo<ActiveOrg>(
    () => ({ activeOrgId, activeOrgName }),
    [activeOrgId, activeOrgName]
  );
  return (
    <ActiveOrgContext.Provider value={value}>
      {children}
    </ActiveOrgContext.Provider>
  );
}

/**
 * Read the active org owned by the shell.
 *
 * When no provider is mounted (isolated component tests, dev probes), returns
 * `{ activeOrgId: null, activeOrgName: null }` so consumers degrade to their
 * existing "no active org yet" loading/empty state rather than throwing.
 */
export function useActiveOrg(): ActiveOrg {
  return (
    useContext(ActiveOrgContext) ?? { activeOrgId: null, activeOrgName: null }
  );
}
